import ConsumptionReport from './ConsumptionReport.js';

export default class PreviousConsumptionReport extends ConsumptionReport {
  /**
   * @return {Bucket[]}
   */
  get messageBuckets() {
    return this._buckets.filter(bucket => bucket.isPreviousMessages);
  }

  /**
   * @return {?Bucket}
   */
  get smsBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isPreviousSms);
  }

  /**
   * @return {?Bucket}
   */
  get mmsBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isPreviousMms);
  }

  /**
   * @return {Bucket[]}
   */
  get voiceBuckets() {
    return this._buckets.filter(bucket => bucket.isPreviousVoice);
  }

  /**
   * @return {?Bucket}
   */
  get oskVoiceBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isPreviousVoiceOsk);
  }

  /**
   * @return {?Bucket}
   */
  get nonOskVoiceBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isPreviousVoiceNonOsk);
  }

  /**
   * @return {?Bucket}
   */
  get dataBucket() {
    return this.getSingleBucketByPredicate(bucket => bucket.isPreviousData);
  }

  /**
   * @return {Bucket[]}
   */
  get topCalledBuckets() {
    return this._buckets.filter(bucket => bucket.isTopCalled);
  }

  /**
   * @return {boolean}
   */
  get hasMessages() {
    return this.messageBuckets.length > 0;
  }

  /**
   * @return {boolean}
   */
  get hasVoice() {
    return this.voiceBuckets.length > 0;
  }

  /**
   * @return {boolean}
   */
  get hasData() {
    return this.dataBucket !== null;
  }

  /**
   * @return {boolean}
   */
  get hasTopCalled() {
    return this.topCalledBuckets.length > 0;
  }

  /**
   * Method for discovering any potential issues with given response. Returns a string with a list of all found issues
   * or null if none are found.
   *
   * @returns {?string}
   */
  get issuesLog() {
    const errors = [];
    const debugBucket = bucket => {
      if (!bucket) {
        return;
      }
      const bucketDebuggers = [
        () => {
          if (bucket.isTopCalled) {
            return;
          }
          // eslint-disable-next-line no-unused-expressions
          bucket.used;
        },
        () => {
          // eslint-disable-next-line no-unused-expressions
          bucket.name;
        },
        () => {
          if (bucket.isTopCalled) {
            return;
          }
          // eslint-disable-next-line no-unused-expressions
          bucket.validFromDate;
        },
        () => {
          if (bucket.isTopCalled) {
            return;
          }
          // eslint-disable-next-line no-unused-expressions
          bucket.validUntilDate;
        },
        () => {
          if (!bucket.isTopCalled) {
            return;
          }
          // eslint-disable-next-line no-unused-expressions
          bucket.topCalledSn;
        },
        () => {
          if (!bucket.isAirtime) {
            return;
          }
          // eslint-disable-next-line no-unused-expressions
          bucket.usedEur;
        },
      ];

      bucketDebuggers.forEach(deb => {
        try {
          deb();
        } catch (error) {
          errors.push(error);
        }
      });
    };

    const debuggers = [
      () => {
        this.voiceBuckets.forEach(debugBucket);
      },
      () => {
        this.messageBuckets.forEach(debugBucket);
      },
      () => {
        debugBucket(this.dataBucket);
      },
      () => {
        // eslint-disable-next-line no-unused-expressions
        this.effectiveDate;
      },
      () => {
        // eslint-disable-next-line no-unused-expressions
        this.effectiveDateForData;
      },
      () => {
        // eslint-disable-next-line no-unused-expressions
        this.showRemaining;
      },
      () => {
        this.topCalledBuckets.forEach(debugBucket);
      },
      () => {
        this.airtimeBuckets.forEach(debugBucket);
      },
    ];

    debuggers.forEach(deb => {
      try {
        deb();
      } catch (error) {
        errors.push(error);
      }
    });

    if (errors.length === 0) {
      return null;
    }

    let message = `Previous consumption issues for SN: ${this.sn}\n`;
    errors.forEach(error => {
      message += `${error}\n`;
    });

    return message;
  }

  /**
   * Returns overview of all available buckets for logging purposes.
   *
   * @returns {string}
   */
  get bucketLog() {
    if (this._buckets.length === 0) {
      return `No buckets for SN "${this.sn}".`;
    }
    let message = `Previous consumption buckets for SN "${this.sn}":\n`;
    this._buckets.forEach(bucket => {
      try {
        if (bucket.isTopCalled) {
          message += `${bucket.id} (top called) - ${bucket.name}: ${bucket.topCalledSn}\n`;
        } else {
          message += `${bucket.id} - ${bucket.name}: used ${bucket.used}\n`;
        }
      } catch (error) {
        message += `${bucket.id} - Bucket is broken.\n`;
      }
    });

    return message;
  }
}
